/**
 * COA “% of change”: `en-US` grouping in the field; `%` is an end adornment. Canonical string has no
 * commas or `%` and is updated on every change.
 */

const INT_MAX = 5;
const FRAC_MAX = 4;

const withSign = (negative: boolean, unsigned: string): string =>
  negative && unsigned ? `-${unsigned}` : negative ? '-' : unsigned;

/** Strip junk; one leading minus; single `.` with optional trailing fraction. */
const sanitizePercentRaw = (raw: string | number): string => {
  let s = String(raw).replace(/[^0-9.\-]/g, '');
  const negative = s.startsWith('-');

  s = s.replace(/-/g, '');
  const dot = s.indexOf('.');

  if (dot === -1) {
    return withSign(negative, s);
  }

  const intPart = s.slice(0, dot);
  const fracPart = s.slice(dot + 1).replace(/\./g, '');
  const body = fracPart.length ? `${intPart}.${fracPart}` : `${intPart}.`;

  return negative ? `-${body}` : body;
};

/** Truncate integer and fraction length (canonical string, not `''` / `'-'`). */
const clampPercentDigits = (signed: string): string => {
  const negative = signed.startsWith('-');
  const t = negative ? signed.slice(1) : signed;
  const dot = t.indexOf('.');

  if (dot === -1) {
    const n = t.slice(0, INT_MAX);

    return negative ? `-${n}` : n;
  }

  const intPart = t.slice(0, dot).slice(0, INT_MAX);
  const fracRaw = t.slice(dot + 1).replace(/\./g, '');
  const fracPart = fracRaw.slice(0, FRAC_MAX);

  if (!fracRaw.length) {
    return negative ? `-${intPart}.` : `${intPart}.`;
  }

  return negative ? `-${intPart}.${fracPart}` : `${intPart}.${fracPart}`;
};

/** Normalize integer leading zeros; preserve trailing `.` while typing decimals. */
const normalizeLeadingZeros = (signed: string): string => {
  const negative = signed.startsWith('-');
  const u = negative ? signed.slice(1) : signed;
  const dot = u.indexOf('.');

  let intPart;
  let fracPart;
  let trailingDot;

  if (dot === -1) {
    intPart = u;
    fracPart = '';
    trailingDot = false;
  } else {
    intPart = u.slice(0, dot);
    fracPart = u.slice(dot + 1);
    trailingDot = fracPart === '';
  }

  if (intPart === '') {
    intPart = '0';
  } else if (/^0+$/.test(intPart)) {
    intPart = '0';
  } else {
    intPart = intPart.replace(/^0+/, '') || '0';
  }

  if (dot === -1) {
    return negative ? `-${intPart}` : intPart;
  }

  if (trailingDot) {
    return negative ? `-${intPart}.` : `${intPart}.`;
  }

  return negative ? `-${intPart}.${fracPart}` : `${intPart}.${fracPart}`;
};

/**
 * Arbitrary field text → canonical string (no commas / `%`).
 *
 * @returns `''` | `'-'` | normalized number string
 */
export const normalizePercentRaw = (raw: string | number): string => {
  const cleaned = sanitizePercentRaw(raw);

  if (cleaned === '' || cleaned === '-') {
    return cleaned;
  }

  return normalizeLeadingZeros(clampPercentDigits(cleaned));
};

const localeGroupedInt = (n: number): string =>
  n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/** Display body only (no `%`); `en-US` grouping. */
const formatPercentBody = (pctString: string | null | undefined): string => {
  if (pctString === '' || pctString == null) return '';

  const n = Number(pctString);

  if (Number.isNaN(n)) return '';

  const opts: Intl.NumberFormatOptions =
    n % 1 === 0
      ? { maximumFractionDigits: 0 }
      : { minimumFractionDigits: 1, maximumFractionDigits: 4 };

  return n.toLocaleString('en-US', opts);
};

/** Read-only label with `%` (e.g. total row). */
export const formatPercentDisplay = (
  pctString: string | null | undefined
): string => {
  const body = formatPercentBody(pctString);

  return body === '' ? '' : `${body}%`;
};

/**
 * Value shown inside the `<input>` (no `%`). If canonical ends with `.`, group integer only and keep
 * the dot (e.g. `12.` while editing).
 */
export const formatPercentInputValue = (canonical: string): string => {
  if (canonical === '' || canonical === '-') {
    return canonical;
  }

  if (!canonical.endsWith('.')) {
    return formatPercentBody(canonical);
  }

  const core = canonical.slice(0, -1);
  const negative = core.startsWith('-');
  const digits = core.replace(/-/g, '');

  if (digits === '') {
    return negative ? '-.' : '.';
  }

  const intNum = Number(digits);

  if (Number.isNaN(intNum)) {
    return canonical;
  }

  const grouped = localeGroupedInt(intNum);

  return negative ? `-${grouped}.` : `${grouped}.`;
};
